/**
 * Dagens genomgång (PLAN.md WP1.5) — the acceptance queue.
 *
 * The queue is where the product's bearing principle becomes a thing you can touch: everything
 * the pipeline produced waits here, and nothing becomes a record until a person says yes.
 *
 * The design target is a measured one: twenty proposals reviewed in under five minutes (gate
 * G1). That is what shapes the page. One proposal is focused at a time and the whole keyboard
 * works on it, so a reviewer never reaches for the mouse; the citation is already on screen,
 * not behind a request; and editing is the same keystroke as accepting, because changing a
 * word before saying yes is the normal case rather than the exception.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { QueueBundle, QueueProposal } from "../../api";
import { api, ApiError } from "../../api";
import { EmptyState } from "../../components/EmptyState";
import { Button, Card, Kbd, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { isTypingTarget } from "../../keyboard";

const KIND_LABELS: Record<string, string> = {
  decision: "Beslut",
  status_update: "Statusrad",
  assumption: "Antagande",
  promise: "Löfte",
  commitment: "Åtagande",
};

type Draft = { text: string; why: string };

export function GenomgangPage() {
  const showToast = useToast();
  const navigate = useNavigate();

  const [bundles, setBundles] = useState<QueueBundle[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>({ text: "", why: "" });
  const [busy, setBusy] = useState(false);
  const [reviewed, setReviewed] = useState(0);
  const textRef = useRef<HTMLTextAreaElement>(null);

  /** The queue as one list, in the order it is worked through. */
  const flat = useMemo(
    () =>
      (bundles ?? []).flatMap((bundle) =>
        bundle.proposals.map((proposal) => ({ bundle, proposal })),
      ),
    [bundles],
  );
  const current = flat[cursor]?.proposal ?? null;

  const reload = useCallback(() => {
    void api
      .reviewQueue()
      .then((queue) => {
        setBundles(queue.bundles);
        setFailed(false);
      })
      .catch((error) => {
        showToast({
          message: error instanceof ApiError ? error.message : "Kön kunde inte hämtas.",
          tone: "fail",
        });
        setFailed(true);
      });
  }, [showToast]);

  useEffect(() => reload(), [reload]);

  // Keep the cursor inside the list as items leave it.
  useEffect(() => {
    if (cursor > 0 && cursor >= flat.length) {
      setCursor(Math.max(0, flat.length - 1));
    }
  }, [flat.length, cursor]);

  const startEditing = useCallback(() => {
    if (!current) return;
    setDraft({ text: current.text, why: current.why ?? "" });
    setEditing(true);
    window.setTimeout(() => textRef.current?.focus(), 0);
  }, [current]);

  const decide = useCallback(
    async (action: "accept" | "reject") => {
      if (!current || busy) return;
      setBusy(true);
      try {
        if (action === "reject") {
          await api.rejectProposal(current.id);
          showToast({ message: "Avvisat.", tone: "pass" });
        } else {
          const body = editing ? { text: draft.text, why: draft.why || undefined } : {};
          const result = await api.acceptProposal(current.id, body);
          showToast({
            message: result.decision
              ? "Accepterat — beslutet är låst."
              : "Accepterat.",
            tone: "pass",
          });
        }
        setEditing(false);
        setReviewed((count) => count + 1);
        // Drop it locally so the next proposal is focused instantly; the list is refreshed
        // afterwards so a colleague's parallel review shows up too.
        setBundles((previous) =>
          (previous ?? [])
            .map((bundle) => ({
              ...bundle,
              proposals: bundle.proposals.filter((one) => one.id !== current.id),
            }))
            .filter((bundle) => bundle.proposals.length > 0),
        );
        reload();
      } catch (error) {
        showToast({
          message: error instanceof ApiError ? error.message : "Åtgärden gick inte igenom.",
          tone: "fail",
        });
        if (error instanceof ApiError && error.status === 409) {
          reload();
        }
      } finally {
        setBusy(false);
      }
    },
    [current, busy, editing, draft, showToast, reload],
  );

  // The shortcuts. Deliberately single keys: the whole point is a rhythm you can keep.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && editing) {
        setEditing(false);
        return;
      }
      if (isTypingTarget(event.target)) {
        // Ctrl+Enter saves from inside the edit fields, the way the capture field works.
        if (editing && event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void decide("accept");
        }
        return;
      }
      switch (event.key.toLowerCase()) {
        case "j":
        case "arrowdown":
          event.preventDefault();
          setEditing(false);
          setCursor((index) => Math.min(index + 1, Math.max(0, flat.length - 1)));
          break;
        case "k":
        case "arrowup":
          event.preventDefault();
          setEditing(false);
          setCursor((index) => Math.max(index - 1, 0));
          break;
        case "a":
          event.preventDefault();
          void decide("accept");
          break;
        case "r":
          event.preventDefault();
          void decide("reject");
          break;
        case "e":
          event.preventDefault();
          startEditing();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [decide, startEditing, editing, flat.length]);

  if (failed) {
    return (
      <EmptyState
        title="Genomgången kunde inte läsas"
        description="Kontrollera att API:t svarar och försök igen."
      />
    );
  }
  if (bundles === null) {
    return <SkeletonText lines={8} />;
  }
  if (flat.length === 0) {
    return (
      <EmptyState
        title="Kön är tom"
        description={
          reviewed > 0
            ? `Klart. ${reviewed} förslag genomgångna.`
            : "Inget väntar på granskning just nu. Nya förslag dyker upp här när pipelinen hittat något."
        }
      />
    );
  }

  return (
    <div className="grid content-start gap-s5">
      <Card hero className="flex flex-wrap items-baseline gap-s4">
        <h1 className="nb-h1">Dagens genomgång</h1>
        <p className="nb-body-muted">
          <Num>{flat.length}</Num> väntar
          {reviewed > 0 ? (
            <>
              {" · "}
              <Num>{reviewed}</Num> klara
            </>
          ) : null}
        </p>
        <p className="ml-auto text-small text-muted">
          <Kbd>J</Kbd> <Kbd>K</Kbd> bläddra · <Kbd>A</Kbd> acceptera · <Kbd>E</Kbd> ändra ·{" "}
          <Kbd>R</Kbd> avvisa
        </p>
      </Card>

      {bundles.map((bundle) => (
        <section
          key={bundle.project_id ?? "utan-projekt"}
          className="grid gap-s4"
          aria-label={bundle.project_name ?? "Utan projekt"}
        >
          <h2 className="nb-overline">
            {bundle.project_name ?? "Utan projekt"}{" "}
            <span className="text-muted">
              (<Num>{bundle.proposals.length}</Num>)
            </span>
          </h2>

          {bundle.proposals.map((proposal) => {
            const focused = proposal.id === current?.id;
            return (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                focused={focused}
                editing={focused && editing}
                draft={draft}
                busy={busy}
                textRef={textRef}
                onFocus={() => {
                  setEditing(false);
                  setCursor(flat.findIndex((entry) => entry.proposal.id === proposal.id));
                }}
                onDraft={setDraft}
                onEdit={startEditing}
                onAccept={() => void decide("accept")}
                onReject={() => void decide("reject")}
                onOpenSource={(captureId) => navigate(`/strommen?fangst=${captureId}`)}
              />
            );
          })}
        </section>
      ))}
    </div>
  );
}

function ProposalCard({
  proposal,
  focused,
  editing,
  draft,
  busy,
  textRef,
  onFocus,
  onDraft,
  onEdit,
  onAccept,
  onReject,
  onOpenSource,
}: {
  proposal: QueueProposal;
  focused: boolean;
  editing: boolean;
  draft: Draft;
  busy: boolean;
  textRef: React.RefObject<HTMLTextAreaElement>;
  onFocus: () => void;
  onDraft: (draft: Draft) => void;
  onEdit: () => void;
  onAccept: () => void;
  onReject: () => void;
  onOpenSource: (captureId: string) => void;
}) {
  const [showSource, setShowSource] = useState(false);
  const source = proposal.sources[0];

  return (
    <Card
      className={`grid gap-s4 transition-[box-shadow,border-color] duration-control ease-nb ${
        focused ? "border-drop shadow-dialog" : ""
      }`}
      onClick={onFocus}
      aria-current={focused ? "true" : undefined}
    >
      <div className="flex flex-wrap items-baseline gap-s3">
        <span className="nb-overline border-0 pb-0">
          {KIND_LABELS[proposal.kind] ?? proposal.kind}
        </span>
        {proposal.confidence !== null && (
          <span className="text-small text-muted">
            säkerhet <Num>{proposal.confidence.toFixed(2)}</Num>
          </span>
        )}
        <span className="ml-auto text-small text-muted">{proposal.space_name}</span>
      </div>

      {editing ? (
        <div className="grid gap-s3">
          <label className="grid gap-s2">
            <span className="nb-overline border-0 pb-0">Text</span>
            <textarea
              ref={textRef}
              className="nb-field min-h-[4.5rem]"
              value={draft.text}
              onChange={(event) => onDraft({ ...draft, text: event.target.value })}
            />
          </label>
          {proposal.kind === "decision" && (
            <label className="grid gap-s2">
              <span className="nb-overline border-0 pb-0">Varför</span>
              <textarea
                className="nb-field min-h-[3.5rem]"
                value={draft.why}
                onChange={(event) => onDraft({ ...draft, why: event.target.value })}
              />
            </label>
          )}
          <p className="text-small text-muted">
            <Kbd>Ctrl + Enter</Kbd> sparar och accepterar · <Kbd>Esc</Kbd> avbryter
          </p>
        </div>
      ) : (
        <>
          <p className="text-body text-ink">{proposal.text}</p>
          {proposal.why && (
            <p className="text-body text-muted">
              <span className="font-medium text-ink">Varför:</span> {proposal.why}
            </p>
          )}
          {proposal.original && (
            <p className="text-small text-muted">Ursprunglig rad: {proposal.original}</p>
          )}
        </>
      )}

      {source && (
        <div className="grid gap-s2">
          <button
            type="button"
            className="justify-self-start text-small text-muted underline decoration-dotted underline-offset-4"
            aria-expanded={showSource}
            onClick={(event) => {
              event.stopPropagation();
              setShowSource((open) => !open);
            }}
          >
            {showSource ? "Dölj källan" : "Visa källan"}
          </button>
          {showSource && (
            <blockquote className="border-l-2 border-drop bg-surface-2 px-s4 py-s3 text-body text-ink">
              ”{source.quote}”
              {source.capture_id && (
                <button
                  type="button"
                  className="ml-s3 text-small text-muted underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenSource(source.capture_id as string);
                  }}
                >
                  Öppna i Strömmen
                </button>
              )}
            </blockquote>
          )}
        </div>
      )}

      {proposal.unknown_entities.length > 0 && (
        <p className="text-small text-muted">
          Okända namn: {proposal.unknown_entities.join(", ")}
        </p>
      )}

      {focused && (
        <div className="flex flex-wrap gap-s3">
          <Button variant="primary" disabled={busy} onClick={onAccept}>
            Acceptera
          </Button>
          {!editing && (
            <Button variant="ghost" disabled={busy} onClick={onEdit}>
              Ändra
            </Button>
          )}
          <Button variant="ghost" disabled={busy} onClick={onReject}>
            Avvisa
          </Button>
        </div>
      )}
    </Card>
  );
}
